/**
 * Registry of built-in patient tabs whose fields support per-field disable/enable.
 *
 * Each built-in field has a `key` used both:
 *  (a) in the Overview/Hospital React components below, to skip rendering when disabled;
 *  (b) in the TabEditorDialog, to show a checklist of togglable built-in fields.
 *
 * Keep this list in sync with PatientProfile.js render blocks.
 */

export const BUILTIN_TAB_FIELD_REGISTRY = {
  overview: {
    label: 'Overview',
    groups: [
      {
        title: 'Contact Information',
        fields: [
          { key: 'email', label: 'Email' },
          { key: 'phone', label: 'Phone' },
          { key: 'address', label: 'Address' },
          { key: 'primary_language', label: 'Primary Language' },
        ],
      },
      {
        title: 'Medical',
        fields: [
          { key: 'medical_diagnosis', label: 'Medical Diagnosis' },
          { key: 'medical_practitioner', label: 'Medical Practitioner' },
        ],
      },
      {
        title: 'Insurance & Provider',
        fields: [
          { key: 'insurance_provider', label: 'Insurance Provider' },
          { key: 'insurance_id', label: 'Insurance ID' },
        ],
      },
      {
        title: 'Emergency Contact',
        fields: [
          { key: 'emergency_contact_name', label: 'Name' },
          { key: 'emergency_contact_phone', label: 'Phone' },
          { key: 'emergency_contact_relationship', label: 'Relationship' },
        ],
      },
    ],
  },
  hospital: {
    label: 'Recent Hospitalization',
    groups: [
      {
        title: 'Recent Hospitalization',
        fields: [
          { key: 'hospital_name', label: 'Hospital Name' },
          { key: 'admission_date', label: 'Admission Date' },
          { key: 'discharge_date', label: 'Discharge Date' },
        ],
      },
      {
        title: 'Medical History',
        fields: [
          { key: 'medical_history', label: 'Medical History' },
          { key: 'allergies', label: 'Allergies' },
        ],
      },
    ],
  },
  concern: {
    label: 'Chief Concern',
    groups: [
      {
        title: 'Chief Concern',
        fields: [
          { key: 'chief_concern', label: 'Chief Concern' },
          { key: 'last_visit_date', label: 'Last Visit Date' },
          { key: 'reason_for_visit', label: 'Reason for Visit' },
          { key: 'other_agencies', label: 'Other Agencies Involved' },
        ],
      },
    ],
  },
  caregiver: {
    label: 'Caregiver',
    groups: [
      {
        title: 'Primary Caregiver',
        fields: [
          { key: 'primary_caregiver_name', label: 'Name' },
          { key: 'primary_caregiver_relationship', label: 'Relationship' },
          { key: 'primary_caregiver_phone', label: 'Phone' },
          { key: 'primary_caregiver_address', label: 'Address' },
        ],
      },
    ],
  },
  pharmacy: {
    label: 'Pharmacy',
    groups: [
      {
        title: 'Pharmacy',
        fields: [
          { key: 'pharmacy_name', label: 'Pharmacy Name' },
          { key: 'pharmacy_address', label: 'Address' },
          { key: 'pharmacy_phone', label: 'Phone' },
        ],
      },
    ],
  },
  clinical: {
    label: 'Clinical',
    groups: [
      {
        title: 'Sections to Show',
        fields: [
          { key: 'vitals', label: 'Vitals' },
          { key: 'medications', label: 'Medications' },
          { key: 'labs', label: 'Labs' },
          { key: 'timeline', label: 'Timeline' },
        ],
      },
    ],
  },
  files: {
    label: 'Files',
    groups: [
      {
        title: 'Sections to Show',
        fields: [
          { key: 'uploaded_files', label: 'Uploaded Files' },
          { key: 'folders', label: 'Folders' },
          { key: 'form_submissions', label: 'Submitted Forms' },
        ],
      },
    ],
  },
};

export function flatBuiltinFields(tabKey) {
  const reg = BUILTIN_TAB_FIELD_REGISTRY[tabKey];
  if (!reg) return [];
  return reg.groups.flatMap(g => g.fields);
}
